import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import styled from 'styled-components/macro';
import { LazyImage } from '../elements/LazyImage';
import { getVoteUrl } from '../utils';

const Root = styled.div`
  cursor: pointer;
  box-shadow: none;
  margin: 0 auto;
  &:hover {
    .card-image > .image {
      transform: scale(1.1);
    }
  }
  .card-content {
    padding: 1rem;
  }
  .card-image {
    overflow: hidden;
  }
  .card-image > .image {
    transition: all 0.3s ease-in-out;
  }
  .buttons {
    position: absolute;
    bottom: 0.5rem;
    right: 0.5rem;
  }
  .button {
    &.is-twitter {
      background-color: rgba(29, 161, 242, 0.5);
      color: white;

      &:hover {
        background-color: rgba(29, 161, 242, 1);
        border-color: white;
      }
    }
    &.is-instagram {
      background: linear-gradient(135deg, rgba(66, 126, 255, 0.5) 0%, rgba(241, 63, 121, 0.5) 70%) no-repeat;
      color: #262626;
      &:hover {
        background: linear-gradient(135deg, rgba(66, 126, 255, 1) 0%, rgba(241, 63, 121, 1) 70%) no-repeat;
        border-color: white;
      }
    }
    &.is-profile {
      background-color: rgba(0, 0, 0, 0.1);
    }
  }
`;

export interface EntryCardProps {
  entryNumber: number;
  name: string;
  college: string;
  twitter: string;
  instagram: string;
  url?: string;
  lazy?: boolean;
  circle?: string;
  buttons?: React.ReactNode;
  onClick?: () => Promise<void>;
}

export function EntryCard(props: EntryCardProps) {
  const [loading, setLoading] = useState<boolean>(false);
  let image = `/images/entries/${props.entryNumber}.jpg`;
  const onClick = async () => {
    if (!props.onClick) {
      return;
    }
    setLoading(true);
    await props.onClick();
    setLoading(false);
  };

  return (
    <Root
      className={classNames('card', {
        'is-loading': loading,
      })}
      onClick={onClick}
    >
      <div className="card-image">
        <figure className="image is-square">
          {props.lazy ? <LazyImage src={image} alt={props.name} /> : <img src={image} alt={props.name} />}
        </figure>
        <div className="is-overlay is-padding-0-5">
          <p className="buttons">
            {props.twitter && (
              <a
                className="button is-twitter is-small-mobile"
                onClick={e => e.stopPropagation()}
                href={`https://twitter.com/${props.twitter}`}
                target="_blank"
                rel="noopener"
              >
                <span className="icon has-text-white is-small-mobile">
                  <i className="fab fa-twitter fa-lg" />
                </span>
              </a>
            )}
            {props.instagram && (
              <a
                className="button is-instagram is-small-mobile"
                onClick={e => e.stopPropagation()}
                href={`https://instagram.com/${props.instagram}`}
                target="_blank"
                rel="noopener"
              >
                <span className="icon has-text-white is-small-mobile">
                  <i className="fab fa-instagram fa-lg" />
                </span>
              </a>
            )}
            {props.buttons}
          </p>
        </div>
      </div>
      <div className="card-content">
        <div className="media">
          <div className="media-content has-text-centered">
            <p className="title is-5 is-size-6-mobile has-text-weight-light">{props.name}</p>
            <p
              className={classNames('subtitle is-7', {
                'has-text-grey': !!props.circle,
                'has-text-primary': !props.circle,
              })}
            >
              {props.circle && (
                <>
                  <span className="has-text-primary">{props.circle}</span>
                  <br />
                </>
              )}
              {props.college}
            </p>
          </div>
        </div>
      </div>
      {props.url && (
        <div className="card-footer">
          <a
            className="card-footer-item has-text-primary is-size-7-mobile"
            onClick={e => e.stopPropagation()}
            href={props.url}
          >
            投票サイト
          </a>
        </div>
      )}
    </Root>
  );
}

export enum Block {
  a = 'a',
  b = 'b',
  c = 'c',
}

export enum Prize {
  first = 'first',
  second = 'second',
  third = 'third',
  semifinal = 'semifinal',
  final = 'final'
}

export interface Entry {
  id: number;
  name: string;
  college?: string;
  twitter?: string;
  instagram?: string;
  circle?: string;
  showroom?: number | null;
  mysta?: number | null;
  semifinal?:string;
  final?:string;
}
